import { z } from "zod/v4";

import {
  AgenticLogicProductIncludeDescriptions,
  AgenticLogicProductOrderByDescriptions,
  AgenticLogicProductQueryDescriptions,
  AgenticLogicProductScanDescriptions,
  AgenticLogicProductWhereDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import { AgenticLogicMetafieldRefSchema } from "./metafield";
import { AgenticLogicProductPostFetchWhereSchema } from "./post-fetch-where";
import { AgenticLogicProductServerWhereSchema } from "./server-where";

// ---------------------------------------------------------------------------
// The typed builder input — `where` (the server/postFetch split), ordering,
// scan bounds, includes, and the query object rules hand to `sdk.getProducts`.
// ---------------------------------------------------------------------------

export const AgenticLogicProductWhereSchema = z
  .object({
    server: AgenticLogicProductServerWhereSchema.optional().describe(
      AgenticLogicProductWhereDescriptions.server
    ),
    postFetch: AgenticLogicProductPostFetchWhereSchema.optional().describe(
      AgenticLogicProductWhereDescriptions.postFetch
    ),
  })
  .describe(AgenticLogicProductWhereDescriptions._schema);
export type AgenticLogicProductWhere = z.infer<
  typeof AgenticLogicProductWhereSchema
>;

/**
 * Sort fields. Wire format — append-only. On the Admin API `title`,
 * `createdAt`, `relevance` and `inventoryTotal` sort server-side on any
 * product read; `price` and `bestSelling` sort server-side ONLY inside one
 * collection (`collection.products`) and otherwise post-fetch over the
 * bounded read. Shop-wide, time-windowed popularity is `getBestSellers`.
 */
export const AgenticLogicProductOrderByFieldList = [
  "bestSelling",
  "price",
  "createdAt",
  "title",
  "relevance",
  "inventoryTotal",
] as const;
export const AgenticLogicProductOrderByFieldSchema = z.enum(
  AgenticLogicProductOrderByFieldList
);
export type AgenticLogicProductOrderByField =
  (typeof AgenticLogicProductOrderByFieldList)[number];

export const AgenticLogicProductOrderByDirectionList = ["asc", "desc"] as const;
export const AgenticLogicProductOrderByDirectionSchema = z.enum(
  AgenticLogicProductOrderByDirectionList
);
export type AgenticLogicProductOrderByDirection =
  (typeof AgenticLogicProductOrderByDirectionList)[number];

export const AgenticLogicProductOrderBySchema = z
  .object({
    field: AgenticLogicProductOrderByFieldSchema.describe(
      AgenticLogicProductOrderByDescriptions.field
    ),
    direction: AgenticLogicProductOrderByDirectionSchema.optional().describe(
      AgenticLogicProductOrderByDescriptions.direction
    ),
  })
  .describe(AgenticLogicProductOrderByDescriptions._schema);
export type AgenticLogicProductOrderBy = z.infer<
  typeof AgenticLogicProductOrderBySchema
>;

export const AgenticLogicStorefrontProductOrderByFieldList = [
  "bestSelling",
  "price",
  "createdAt",
  "title",
  "relevance",
] as const;
export const AgenticLogicStorefrontProductOrderBySchema = z
  .object({
    field: z
      .enum(AgenticLogicStorefrontProductOrderByFieldList)
      .describe(AgenticLogicProductOrderByDescriptions.field),
    direction: AgenticLogicProductOrderByDirectionSchema.optional().describe(
      AgenticLogicProductOrderByDescriptions.direction
    ),
  })
  .describe(AgenticLogicProductOrderByDescriptions._schema);
export type AgenticLogicStorefrontProductOrderBy = z.infer<
  typeof AgenticLogicStorefrontProductOrderBySchema
>;

/**
 * Platform hard ceiling for `limit` on one product read.
 *
 * Named rather than inline because the authoring projection interpolates it —
 * the model was shown a bare `limit: number` and had to guess, which is the
 * same defect that produced `scan.maxProducts: 10000`. Moving the ceiling here
 * moves the sentence the model reads.
 */
export const AGENTIC_LOGIC_PRODUCT_QUERY_MAX_LIMIT = 250;

/**
 * Storefront's connection ceiling for a product's collection memberships: the
 * most `include.collectionIds: { first }` may ask for, and what recommendation
 * reads always select. A product in more collections than the requested count
 * has truncated membership; pageInfo.hasNextPage lets the SDK report that.
 */
export const AGENTIC_LOGIC_RECOMMENDATION_COLLECTION_LIMIT = 250;

/**
 * Headroom multiplier over what a rule can render, for sizing a bounded read.
 *
 * Lives here rather than in the publish gate that enforces it because it is a
 * query-sizing CONTRACT number, sibling to the ceiling above: the gate's
 * arithmetic, the rendered `query.limit` note and the starter exemplar's drift
 * guard all have to read the same figure or the platform teaches one number
 * and measures another. Post-fetch cart and availability filtering
 * legitimately discards most of a read, which is what the factor buys.
 */
export const AGENTIC_LOGIC_QUERY_LIMIT_HEADROOM_FACTOR = 4;
/**
 * Flat additive headroom so a small placement may still read a workable pool:
 * 3 slots => 32 allowed, not 12.
 */
export const AGENTIC_LOGIC_QUERY_LIMIT_HEADROOM_FLOOR = 20;

/** Platform hard ceiling for one post-fetch product scan. */
export const AGENTIC_LOGIC_PRODUCT_SCAN_MAX_PRODUCTS = 1000;
/** Platform hard ceiling for one scan page. Also the default page size. */
export const AGENTIC_LOGIC_PRODUCT_SCAN_MAX_PAGE_SIZE = 250;

/**
 * The Storefront's input-list limit for one operation — the largest `nodes(ids:)`
 * list, metafield-identifier list or collection-filter list a single compiled
 * document may carry.
 *
 * Expressed in REFS, not products: one shortlisted product contributes as many
 * entries as the variants a rule expands it into. The SDK does not refuse a
 * longer list; `sdk.checkAvailability` PAGES the caller's list at this size and
 * concatenates the pages, so the ceiling is a per-read fact rather than a
 * per-call one.
 *
 * It lives here rather than beside the compiler because the authoring
 * projection interpolates it, and `@intelligems/ig-utils` / `@intelligems/ig-types`
 * may not import from `node-backend`.
 */
export const AGENTIC_LOGIC_STOREFRONT_INPUT_LIST_MAX = 250;

export const AgenticLogicProductScanSchema = z
  .object({
    pageSize: z
      .number()
      .int()
      .positive()
      .max(AGENTIC_LOGIC_PRODUCT_SCAN_MAX_PAGE_SIZE)
      .optional()
      .describe(AgenticLogicProductScanDescriptions.pageSize),
    maxProducts: z
      .number()
      .int()
      .positive()
      .max(AGENTIC_LOGIC_PRODUCT_SCAN_MAX_PRODUCTS)
      .optional()
      .describe(AgenticLogicProductScanDescriptions.maxProducts),
  })
  .describe(AgenticLogicProductScanDescriptions._schema);
export type AgenticLogicProductScan = z.infer<
  typeof AgenticLogicProductScanSchema
>;

export const AgenticLogicProductIncludeSchema = z
  .object({
    collectionIds: z
      .union([
        z.boolean(),
        z.object({
          first: z
            .number()
            .int()
            .positive()
            .max(AGENTIC_LOGIC_RECOMMENDATION_COLLECTION_LIMIT),
        }),
      ])
      .optional()
      .describe(AgenticLogicProductIncludeDescriptions.collectionIds),
    metafields: z
      .array(AgenticLogicMetafieldRefSchema)
      .optional()
      .describe(AgenticLogicProductIncludeDescriptions.metafields),
  })
  .describe(AgenticLogicProductIncludeDescriptions._schema);
export type AgenticLogicProductInclude = z.infer<
  typeof AgenticLogicProductIncludeSchema
>;

export const AgenticLogicProductQuerySchema = z
  .object({
    ids: z
      .array(z.string())
      .optional()
      .describe(AgenticLogicProductQueryDescriptions.ids),
    collectionId: z
      .string()
      .optional()
      .describe(AgenticLogicProductQueryDescriptions.collectionId),
    where: AgenticLogicProductWhereSchema.optional().describe(
      AgenticLogicProductQueryDescriptions.where
    ),
    orderBy: AgenticLogicStorefrontProductOrderBySchema.optional().describe(
      AgenticLogicProductQueryDescriptions.orderBy
    ),
    limit: z
      .number()
      .int()
      .positive()
      .max(AGENTIC_LOGIC_PRODUCT_QUERY_MAX_LIMIT)
      .describe(AgenticLogicProductQueryDescriptions.limit),
    scan: AgenticLogicProductScanSchema.optional().describe(
      AgenticLogicProductQueryDescriptions.scan
    ),
    include: AgenticLogicProductIncludeSchema.optional().describe(
      AgenticLogicProductQueryDescriptions.include
    ),
  })
  .describe(AgenticLogicProductQueryDescriptions._schema);
export type AgenticLogicProductQuery = z.infer<
  typeof AgenticLogicProductQuerySchema
>;

// ---------------------------------------------------------------------------
// Compile-time checks: description objects must cover all schema keys
// ---------------------------------------------------------------------------

assertDescriptions<
  typeof AgenticLogicProductWhereDescriptions,
  AgenticLogicProductWhere
>();
assertDescriptions<
  typeof AgenticLogicProductOrderByDescriptions,
  AgenticLogicProductOrderBy
>();
assertDescriptions<
  typeof AgenticLogicProductScanDescriptions,
  AgenticLogicProductScan
>();
assertDescriptions<
  typeof AgenticLogicProductIncludeDescriptions,
  AgenticLogicProductInclude
>();
assertDescriptions<
  typeof AgenticLogicProductQueryDescriptions,
  AgenticLogicProductQuery
>();
